import React, { useState, useEffect } from "react";

//import localization
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import { Grid, Row, Col, Card, Statistic, Badge } from "antd";
import { FileProtectOutlined, HomeOutlined, FlagOutlined, FieldTimeOutlined, UserOutlined } from "@ant-design/icons";

import { DatePatterns } from "../utils/consts";

import { stateContext } from "./MainView";
import http from "../utils/http";

import "../styles/frame-styles.less";

const { useBreakpoint } = Grid;

const cardHeaderStyle = { textAlign: "center", background: "#d8061f", color: "#fff" };

function PaymentView({ query }) {
	//Locale Hooks
	const { t } = useTranslation();

	//formState: from stateContext
	const [formState] = React.useContext(stateContext);
	const [frameElement, setFrameElement] = useState(null);
	const [status, setStatus] = useState(0); // status: 0 means not done, status: 1: Finished successfully, status: 2 Finished but failed, status 3: Time Out

	//Controller for window resizing.
	//Used in: Row to change the justification of the columns
	const screen = useBreakpoint();

	//Const used for code simplification only to avoid nested objects
	const Contractor = formState?.TravellersForm?.Contractor;
	const Product = formState?.ProductForm;
	const Plan = formState?.SelectedPlan;
	const AcceptOffer = formState?.AcceptOffer;

	//On init set scroll position to top
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		// Get iframe from Piraeus bank
		const getPaymentForm = () => {
			http
				.get(`/travelinsurancesingle/paymentform`, {
					params: {
						o: AcceptOffer.OfferId
					}
				})
				.then((response) => {
					setFrameElement(<div dangerouslySetInnerHTML={{ __html: response.data }} />);
				})
				.catch((error) => {
					console.log(error);
				});
		};

		// Check every 5 secs if the user has completed, finished with the transaction
		const checkOfferStatus = () => {
			http
				.get(`/travelinsurancesingle/checkofferstatus`, {
					params: {
						o: AcceptOffer.OfferId
					}
				})
				.then((response) => {
					if (dayjs(AcceptOffer.Expire).diff(Date(), "minute") > 0) {
						if (response.data.finished !== true) {
							// Keep repeating callbacks till user completes transaction
							setTimeout(() => {
								checkOfferStatus();
							}, 5000);
						} else {
							//Stop callbacks and return final result according to the success status
							if (response.data.success === true) {
								setStatus(1);
							} else {
								setStatus(2);
							}
						}
					} else {
						// If session surpassed time limit then return time out message
						setStatus(3);
					}
				})
				.catch((error) => {
					setStatus(3);
					console.log(error);
				});
		};

		getPaymentForm();
		checkOfferStatus();
	}, [AcceptOffer]);

	// Return generated view from bank
	const FrameElementResult = ({ status }) => {
		if (status === 3)
			return (
				<table className="pay-text">
					<tbody>
						<tr>
							<td className="text-elements">{t("YourSessionHasExpired")}</td>
						</tr>
						<tr>
							<td>
								<a href={`/travel?ch=${query.Channel}&lg=${query.Language}`} style={{ color: "#3fd273" }}>
									{t("YouMayNeedToReSubmitTheFormOrReloadThePage")}
								</a>
							</td>
						</tr>
					</tbody>
				</table>
			);

		return (
			<table className="pay-text">
				<tbody>
					<tr>
						<td className="text-elements">{status === 1 ? t("YourTransactionHasBeenCompletedSuccessfully") : t("UnfortunatelyAnErrorHasOccurredDuringTheTransaction")}</td>
					</tr>
					<tr>
						<td className="text-elements">{status === 1 ? t("YourPolicyWillBeEmailedToYouShortly") : t("PleaseTryAgain")}</td>
					</tr>
				</tbody>
			</table>
		);
	};
	return (
		<Row gutter={[20, 20]} justify={screen.xs ? "center" : "space-around"} style={{ padding: 20 }}>
			<Col xs={24} sm={24} md={16} lg={16} className="payform-panel">
				<Card
					className="card-bank"
					size="small"
					title={
						<div style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
							{t("YourTranscationIsBeingIssuedBy")} {t("PiraeusBank")}
						</div>
					}
					headStyle={{ display: "table", textAlign: "center", background: "#fab414", fontWeight: "bold", color: "#003b95" }}
				>
					<div className="center-frame-element">{status === 0 ? frameElement : <FrameElementResult status={status} />}</div>
				</Card>
			</Col>
			<Col xs={24} sm={24} md={8} lg={8}>
				<div style={{ border: "1px solid #f0f0f0" }}>
					<Statistic value={Plan?.MandatoryGrossAmount} valueStyle={{ color: "black", textAlign: "center", fontSize: 30, fontWeight: "bold" }} suffix="€" />

					<div className="cost-secondary" style={{ padding: "0px 5px 0px 5px" }}>
						{t("TaxPremium")} {Plan?.MandatoryTaxAmount}€
					</div>
				</div>
				<Card size="small" title={t("TripDetails")} headStyle={cardHeaderStyle} style={{ width: "100%" }}>
					<div>
						<FileProtectOutlined /> {Product?.Product?.Name}
					</div>
					<div>
						<HomeOutlined /> {Product?.Departure?.Name}
					</div>
					<div>
						<FlagOutlined /> {Product?.Destination?.Name}
					</div>
					<div>
						<FieldTimeOutlined /> {Product?.Duration[0].format(DatePatterns.LongDate)} - {Product?.Duration[1].format(DatePatterns.LongDate)}
					</div>
					<div>
						<UserOutlined /> {Product?.Travellers?.length}
					</div>
				</Card>
				<Card size="small" title={t("PolicyHolderDetails")} headStyle={cardHeaderStyle} style={{ width: "100%" }}>
					<Badge color="#d8061f" text={`${Contractor?.FirstName} ${Contractor?.LastName}`} />
					<br />
					<Badge color="#d8061f" text={Contractor?.Birth?.format(DatePatterns.LongDate)} />
					<br />
					<Badge color="#d8061f" text={JSON.parse(Contractor?.Certificate?.IdTypeId).Name} />
					<br />
					<Badge color="#d8061f" text={Contractor.Certificate?.IdNo} />
					<br />
					<Badge color="#d8061f" text={JSON.parse(Contractor?.Location?.Country).Name} />
					<br />
					<Badge color="#d8061f" text={Contractor?.Location?.City} />
					<br />
					<Badge color="#d8061f" text={Contractor?.Street} />
					<br />
					<Badge color="#d8061f" text={Contractor?.ZipCode} />
					<br />
					<Badge color="#d8061f" text={`${Contractor?.Mobile?.Prefix} ${Contractor?.Mobile?.Number}`} />
					<br />
					<Badge color="#d8061f" text={Contractor?.Email} />
					<br />
				</Card>
			</Col>
		</Row>
	);
}

export default React.memo(PaymentView);
